import { Injectable } from '@angular/core';
import { DemoPairKey } from '../data/demos';
import {
  AnxietyLevelId,
  audioExperiences,
  AudioExperienceId,
  ContentFormatId,
  GoalId,
  InvestmentTimeId,
  MotivationId,
  ProcrastinationFrequencyId,
  ResultPlanCard,
  retentionOptions,
  RetentionId,
  TimeForReadingId,
  visionCareOptions,
  VisionCareId,
} from '../data/quiz';
import {
  formatList,
  getAnswerIds,
  getFirstAnswerId,
  getMomentumScore,
  hasEyeComfortConcern,
  listeningPlans,
  QuizAnswers,
} from '../data/result-helpers';

declare const $localize: any;

/** Where the plan's reading list came from, as reported on the metric event. */
export type ResultPlanReadingList =
  | { source: 'liked_books'; bookTitles: string[] }
  | { source: 'liked_demos'; demoKinds: DemoPairKey[] }
  | { source: 'goal_books'; bookTitles: string[] };

// The answers the flow service already holds parsed come typed; the rest are read from `answers`.
export interface ResultPlanMessageParams {
  answers: QuizAnswers;
  goalId: GoalId | undefined;
  motivationIds: MotivationId[];
  procrastinationId: ProcrastinationFrequencyId | undefined;
  anxietyId: AnxietyLevelId | undefined;
  formatIds: ContentFormatId[];
  readingList: ResultPlanReadingList;
}

/** The plan as it sits under the graph: the momentum score as the caption, then the cards. */
export interface ResultPlan {
  graphCaption: string;
  cards: ResultPlanCard[];
  metricProperties: Record<string, unknown>;
}

type ConsumptionWindowId =
  | 'inMorning'
  | 'workingOut'
  | 'onCommute'
  | 'takingBreak'
  | 'chores'
  | 'workingOrStudying';

type PlanLength = 'minutes0515' | 'minutes1530' | 'minutes3060';

type Friction = 'time' | 'procrastination' | 'stress' | 'eyes' | 'retention';

type ContentKind = DemoPairKey | 'documents' | 'ownWriting' | 'printed' | 'aiChats';

/**
 * The main funnel's "plan" result: the kindle
 * funnel's concrete listening plan, driven by the answers the main funnel collects. Same five-part
 * shape as KindleResultMessageService - diagnosis, daily plan, reading list, momentum score, next
 * step - and it reuses that service's message ids wherever the text carries no Kindle wording.
 * The score captions the graph; the other four are the cards below it.
 */
@Injectable({
  providedIn: 'root',
})
export class ResultPlanMessageService {
  private readonly investmentPlanMap: Record<InvestmentTimeId, PlanLength> = {
    noFreeTime: 'minutes0515',
    microSession: 'minutes0515',
    fewSessions: 'minutes1530',
    regularCommitment: 'minutes3060',
  };

  private readonly timeForReadingPlanMap: Record<TimeForReadingId, PlanLength> = {
    allTheTime: 'minutes0515',
    sometimes: 'minutes1530',
    notReally: 'minutes3060',
  };

  private readonly windowTextMap: Record<ConsumptionWindowId, string> = {
    inMorning: $localize`:@@kindleResultWindowMorning:your morning reset`,
    workingOut: $localize`:@@kindleResultWindowWorkout:workouts or walks`,
    onCommute: $localize`:@@kindleResultWindowCommute:commutes and errands`,
    takingBreak: $localize`:@@kindleResultWindowBreak:short breaks`,
    chores: $localize`:@@funnelPlanWindowChores:chores around the house`,
    workingOrStudying: $localize`:@@funnelPlanWindowWorkStudy:work or study sessions`,
  };

  private readonly frictionTextMap: Record<Friction, string> = {
    time: $localize`:@@kindleResultFrictionQuiet:limited quiet time`,
    procrastination: $localize`:@@funnelPlanFrictionProcrastination:putting reading off`,
    stress: $localize`:@@funnelPlanFrictionStress:stress that drains your energy`,
    eyes: $localize`:@@funnelPlanFrictionEyes:tired eyes`,
    retention: $localize`:@@funnelPlanFrictionRetention:forgetting what you read`,
  };

  private readonly contentTextMap: Record<ContentKind, string> = {
    web: $localize`:@@funnelPlanContentWeb:web articles`,
    email: $localize`:@@funnelPlanContentEmail:emails`,
    education: $localize`:@@funnelPlanContentEducation:study materials`,
    ebooks: $localize`:@@funnelPlanContentEbooks:eBooks`,
    selfhelp: $localize`:@@funnelPlanContentSelfHelp:non-fiction`,
    romance: $localize`:@@funnelPlanContentRomance:romance novels`,
    religious: $localize`:@@funnelPlanContentReligious:spiritual reading`,
    truecrime: $localize`:@@funnelPlanContentTrueCrime:true crime`,
    documents: $localize`:@@funnelPlanContentDocuments:PDFs`,
    ownWriting: $localize`:@@funnelPlanContentOwnWriting:your own notes`,
    printed: $localize`:@@funnelPlanContentPrinted:printed pages`,
    aiChats: $localize`:@@funnelPlanContentAiChats:AI chats`,
  };

  private readonly readingTastesText = $localize`:@@kindleResultSampleDefault:your reading tastes`;

  // YouTube and "other" name no reading material the library could be matched to.
  private readonly formatContentMap: Partial<Record<ContentFormatId, ContentKind>> = {
    webArticles: 'web',
    emails: 'email',
    ebooks: 'ebooks',
    documentsPdfs: 'documents',
    ownWriting: 'ownWriting',
    booksPrintedTexts: 'printed',
    religiousTexts: 'religious',
    aiChatsResearch: 'aiChats',
  };

  generatePlan(params: ResultPlanMessageParams): ResultPlan {
    const { answers, procrastinationId, anxietyId } = params;
    const timeForReadingId = getFirstAnswerId<TimeForReadingId>(
      answers,
      'timeForReading',
      this.timeForReadingPlanMap
    );
    const visionCareId = getFirstAnswerId<VisionCareId>(answers, 'visionCare', visionCareOptions);
    const retentionId = getFirstAnswerId<RetentionId>(answers, 'retention', retentionOptions);
    // Read from the stored answer rather than the flow service's field, which a stored
    // kindleAudioExperience (the AppLovin domain serves both flows) would take precedence over.
    const audioExperienceId = getFirstAnswerId<AudioExperienceId>(
      answers,
      'audioExperience',
      audioExperiences
    );
    const investmentTimeId = getFirstAnswerId<InvestmentTimeId>(
      answers,
      'investmentTime',
      this.investmentPlanMap
    );
    const windowIds = getAnswerIds<ConsumptionWindowId>(answers, 'time', this.windowTextMap);

    const hasEyeConcern = hasEyeComfortConcern(visionCareId);
    const hasTimeConcern = timeForReadingId === 'allTheTime' || timeForReadingId === 'sometimes';
    const hasProcrastination = procrastinationId === 'always' || procrastinationId === 'frequently';
    const hasStress = anxietyId === 'yesSignificant' || anxietyId === 'yesSomeExtent';
    const hasRetentionGap = retentionId === 'yesAllTime' || retentionId === 'yesOften';

    // Listed in the order the blockers sentence names them.
    const frictions: Friction[] = [
      ...(hasTimeConcern ? (['time'] as const) : []),
      ...(hasProcrastination ? (['procrastination'] as const) : []),
      ...(hasStress ? (['stress'] as const) : []),
      ...(hasEyeConcern ? (['eyes'] as const) : []),
      ...(hasRetentionGap ? (['retention'] as const) : []),
    ];
    // Time and eyes have their own adjustments in the score, as on the kindle one.
    const behaviourFrictionCount = [hasProcrastination, hasStress, hasRetentionGap].filter(
      Boolean
    ).length;

    const planLength =
      (investmentTimeId && this.investmentPlanMap[investmentTimeId]) ||
      (timeForReadingId && this.timeForReadingPlanMap[timeForReadingId]) ||
      'minutes1530';
    const listeningPlan = listeningPlans[planLength];

    const diagnosis = this.getDiagnosis({
      timeForReadingId,
      visionCareId,
      hasProcrastination,
      hasStress,
      hasRetentionGap,
    });
    const frictionText =
      frictions.length > 0
        ? formatList(frictions.map(id => this.frictionTextMap[id]))
        : $localize`:@@kindleResultFrictionDefault:finding enough quiet reading time`;
    const windowText =
      windowIds.length > 0
        ? formatList(windowIds.map(id => this.windowTextMap[id]))
        : $localize`:@@kindleResultWindowsDefault:walks, chores, commutes, and screen-free breaks`;
    const relief = this.getReadingRelief(params, hasEyeConcern, hasTimeConcern);
    const formatMatch = this.getFormatMatch(params.formatIds);

    // The kindle score with the main funnel's answers: the behavioural frictions stand in for the
    // kindle friction list, and the plan's lift comes from investmentTime, not listeningTime.
    const metric = getMomentumScore({
      frictionCount: behaviourFrictionCount,
      timeForReadingId,
      visionCareId,
      audioExperienceId,
      potentialLift: listeningPlan.potentialLift,
    });
    const timeframe = $localize`:@@kindleResultMetricTimeframe:3 months`;

    return {
      graphCaption: $localize`:@@kindleResultPersonalizedMsg4:Your Reading Momentum Score is estimated at ${metric.currentValue}:CURRENT_VALUE:%. With this plan, it can realistically reach ${metric.potentialValue}:POTENTIAL_VALUE:% in ${timeframe}:TIMEFRAME: because the routine uses the time you already have.`,
      cards: [
        {
          icon: 'ri-stethoscope-line',
          text: $localize`:@@funnelPlanMsg1:You are not falling behind on reading because you lack discipline. Based on your answers, the pattern is clear: ${diagnosis}:DIAGNOSIS:. Your biggest blockers are ${frictionText}:FRICTION:.`,
        },
        {
          icon: 'ri-calendar-check-line',
          text: $localize`:@@kindleResultPersonalizedMsg2:The best plan for you is ${listeningPlan.dailyLabel}:DAILY_LABEL:, mostly during ${windowText}:WINDOWS:. That is enough to ${listeningPlan.progressOutcome}:PROGRESS_OUTCOME: without needing a perfect quiet reading session.`,
        },
        {
          icon: 'ri-book-open-line',
          text: this.getReadingListMessage(params.readingList, relief),
        },
        {
          icon: 'ri-arrow-right-circle-line',
          text: $localize`:@@funnelPlanMsg5:Next, we will unlock the exact plan: natural voices selected for long sessions, speed control, and a library matched to ${formatMatch}:FORMAT_MATCH:.`,
        },
      ],
      metricProperties: {
        metric_name: 'Reading Momentum Score',
        metric_abbreviation: 'RMS',
        metric_id: 'reading_momentum',
        result_variant: 'plan',
        current_value: metric.currentValue,
        potential_value: metric.potentialValue,
        timeframe,
        friction_count: frictions.length,
        plan_length: planLength,
        reading_list_source: params.readingList.source,
      },
    };
  }

  // The strongest friction signal wins: a constant lack of time or worsening eyesight outranks the
  // behavioural answers, and the milder versions of both only speak when nothing else does.
  private getDiagnosis(signals: {
    timeForReadingId: TimeForReadingId | undefined;
    visionCareId: VisionCareId | undefined;
    hasProcrastination: boolean;
    hasStress: boolean;
    hasRetentionGap: boolean;
  }): string {
    const eyeComfort = $localize`:@@kindleResultDiagnosisEyeComfort:this is an eye-comfort problem, not a motivation problem`;

    if (signals.timeForReadingId === 'allTheTime') {
      return $localize`:@@kindleResultDiagnosisAccess:this is a time-access problem, not a motivation problem`;
    }

    if (signals.visionCareId === 'gettingWorse') {
      return eyeComfort;
    }

    if (signals.hasProcrastination) {
      return $localize`:@@funnelPlanDiagnosisStart:this is a getting-started problem, not a willpower problem`;
    }

    if (signals.hasStress) {
      return $localize`:@@kindleResultDiagnosisEnergy:this is an energy problem, not a discipline problem`;
    }

    if (signals.hasRetentionGap) {
      return $localize`:@@funnelPlanDiagnosisRetention:this is a retention problem, not an effort problem`;
    }

    if (signals.visionCareId === 'aBit') {
      return eyeComfort;
    }

    if (signals.timeForReadingId === 'sometimes') {
      return $localize`:@@kindleResultTimeSometimes:reading time appears in uneven pockets`;
    }

    return $localize`:@@funnelPlanDiagnosisDefault:your reading routine needs easier access to what you already want to read`;
  }

  // Reading relief first: eye comfort and accessibility, then time, matching the motivations that
  // convert best in the main funnel.
  private getReadingRelief(
    params: ResultPlanMessageParams,
    hasEyeConcern: boolean,
    hasTimeConcern: boolean
  ): string {
    const accessibilityMotivations: MotivationId[] = [
      'reduceEyeStrain',
      'overcomeDyslexia',
      'manageAdhd',
    ];

    if (
      hasEyeConcern ||
      params.goalId === 'adhdEyeStrainDyslexia' ||
      params.motivationIds.some(id => accessibilityMotivations.includes(id))
    ) {
      return $localize`:@@listeningEyesBreak:Listening gives your eyes a break, without stopping you.`;
    }

    if (
      hasTimeConcern ||
      params.goalId === 'listenOnTheGo' ||
      params.motivationIds.includes('saveTime')
    ) {
      return $localize`:@@funnelPlanReliefTime:You can get through it on the go instead of waiting for free time to sit down with a book.`;
    }

    return $localize`:@@funnelPlanReliefDefault:Hearing it in a natural voice makes reading feel like a break, not another task.`;
  }

  private getReadingListMessage(readingList: ResultPlanReadingList, relief: string): string {
    const items =
      readingList.source === 'liked_demos'
        ? readingList.demoKinds.map(kind => this.contentTextMap[kind])
        : readingList.bookTitles.map(title => $localize`:@@funnelPlanBookTitle:“${title}:TITLE:”`);
    const list = formatList(items) || this.readingTastesText;

    if (readingList.source === 'goal_books') {
      return $localize`:@@funnelPlanMsg3Picked:Your reading list starts with ${list}:READING_LIST:, picked from your answers. ${relief}:RELIEF:`;
    }

    return $localize`:@@funnelPlanMsg3Liked:Your reading list starts with what you wanted to hear more of: ${list}:READING_LIST:. ${relief}:RELIEF:`;
  }

  private getFormatMatch(formatIds: ContentFormatId[]): string {
    const kinds = formatIds
      .map(id => this.formatContentMap[id])
      .filter((kind): kind is ContentKind => !!kind);

    if (kinds.length === 0) {
      return this.readingTastesText;
    }

    return formatList(kinds.map(kind => this.contentTextMap[kind]));
  }
}
